import React, { useEffect, useRef } from 'react';
import H from '@here/maps-api-for-javascript';
import { useDispatch } from 'react-redux';
import withTranslation from 'hoc/withTranslation';
import { enhanceActivityGeoJSONFeatures } from '../NewActivityMarkers/utils/activityMarkerHelpers';
import { setActivityMarkerIcon } from '../NewActivityMarkers/utils/activityMarkerFactory';
import { attachActivityHoverEvents } from '../NewActivityMarkers/hooks/useActivityMarkerEvents';
import { areMarkerPropsEqual } from '../utils/markerPropsUtils';
import { createMarkerPopup } from '../NewJobMarkers/utils/markerPopupUtils';
import { buildActivityClusterIcon, ACTIVITY_CLUSTER_ANCHOR } from './utils/clusterIconFactory';
import { buildActivityClusterPopupHtml } from './utils/clusterPopupBuilder';

const CLUSTER_EPS = 80;

const NewClusteredActivityMarkers = ({ translations, map, geoJSON, user, display }) => {
  const dispatch = useDispatch();
  const providerRef = useRef(null);
  const translationsRef = useRef(translations);
  const displayRef = useRef(display);
  const highlightRef = useRef({ id: display?.highlightStop, tourId: display?.selectedTour });

  useEffect(() => {
    translationsRef.current = translations;
    displayRef.current = display;
    highlightRef.current = { id: display?.highlightStop, tourId: display?.selectedTour };
  });

  useEffect(() => {
    if (!map) return undefined;

    const theme = {
      getClusterPresentation: (cluster) => {
        const dataPoints = [];
        cluster.forEachDataPoint((dp) => dataPoints.push(dp.getData().properties));

        const svgHtml = buildActivityClusterIcon(dataPoints, highlightRef.current);
        const marker = new window.H.map.Marker(cluster.getPosition(), {
          min: cluster.getMinZoom(),
          volatility: true,
        });
        marker.setIcon(new window.H.map.Icon(svgHtml, { anchor: ACTIVITY_CLUSTER_ANCHOR }));
        marker.setData(dataPoints);
        createMarkerPopup(marker, map, ACTIVITY_CLUSTER_ANCHOR.y, () =>
          buildActivityClusterPopupHtml(dataPoints, translationsRef.current),
        );
        return marker;
      },
      getNoisePresentation: (noisePoint) => {
        const feature = noisePoint.getData();
        const props = feature.properties;
        const hl = highlightRef.current;
        const highlight =
          hl?.id !== undefined && props.stopIndex === hl.id && props.routeId === hl.tourId;
        const marker = new window.H.map.DomMarker(
          { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] },
          { volatility: true },
        );
        marker.setData(props);
        marker.setVisibility(true);
        setActivityMarkerIcon(marker, feature, highlight);
        attachActivityHoverEvents(marker, translationsRef, map, dispatch, displayRef);
        return marker;
      },
    };

    const provider = new window.H.clustering.Provider([], {
      clusteringOptions: { eps: CLUSTER_EPS, minWeight: 2 },
      theme,
    });
    const clusterLayer = new H.map.layer.ObjectLayer(provider);
    map.addLayer(clusterLayer);
    providerRef.current = provider;

    return () => {
      map.removeLayer(clusterLayer);
      providerRef.current = null;
    };
  }, [map, dispatch]);

  useEffect(() => {
    if (!providerRef.current) return;

    if (
      !geoJSON?.geo?.features ||
      geoJSON.geo.features.length === 0 ||
      display?.showProblem ||
      !user?.mapSettings?.showStops
    ) {
      providerRef.current.setDataPoints([]);
      return;
    }

    const showOnlyFirstVisits = user?.mapSettings?.showOnlyFirstVisits || false;
    const enhancedGeoJSON = enhanceActivityGeoJSONFeatures(geoJSON, displayRef.current, showOnlyFirstVisits);
    if (!enhancedGeoJSON?.geo?.features || enhancedGeoJSON.geo.features.length === 0) {
      providerRef.current.setDataPoints([]);
      return;
    }

    const dataPoints = enhancedGeoJSON.geo.features
      .filter((f) => f.geometry.type === 'Point')
      .map((f) => {
        const [lng, lat] = f.geometry.coordinates;
        return new window.H.clustering.DataPoint(lat, lng, 1, f);
      });

    providerRef.current.setDataPoints(dataPoints);
  }, [
    geoJSON,
    display?.showProblem,
    display?.showUnassigned,
    display?.selectedTour,
    display?.routeIds,
    display?.highlightStop,
    user?.mapSettings?.showStops,
    user?.mapSettings?.showOnlyFirstVisits,
  ]);

  return null;
};

const MemoizedNewClusteredActivityMarkers = React.memo(
  NewClusteredActivityMarkers,
  areMarkerPropsEqual,
);

export default withTranslation(MemoizedNewClusteredActivityMarkers);

